import React, { useCallback, useMemo, useRef, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { Link } from "react-router-dom";
import TableContainer from "components/common/TableContainer";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { toast } from "react-toastify";
import DeleteModal from "components/common/DeleteModal";
import ConfirmActionModal from "components/common/ConfirmActionModal";
import Avatar from "components/common/Avatar";

import { ColumnDef } from "@tanstack/react-table";
import { IndexColumn } from "components/common/columns";
import { DateTime } from "components/common/Date";
import { getRoleBadgeClass } from "features/RolesAndPermissions/constants/role.constants";
import { deleteUser, getUsers, setUserActiveStatus } from "features/Users/services/UserService";
import { User } from "features/Users/types/user.types";
import { getDocumentTitle } from "utils/helpers";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { useProfile } from "features/Authentication/hooks/useProfile";

const UserList = () => {
  document.title = getDocumentTitle("Users");
  const { can } = useAccess();
  const { isYou } = useProfile();
  const canReadUser = can(Permissions.USER.READ);
  const canUpdateUser = can(Permissions.USER.UPDATE);
  const canDeleteUser = can(Permissions.USER.DELETE);

  const tableRef = useRef<() => void>(null);
  const [selectedRecord, setSelectedRecord] = useState<User | null>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);

  const onClickDelete = useCallback((record: User) => {
    setSelectedRecord(record);
    setDeleteModal(true);
  }, []);

  const openDeactivate = useCallback((user: User) => {
    setSelectedRecord(user);
    setDeactivateOpen(true);
  }, []);

  const openActivate = useCallback((user: User) => {
    setSelectedRecord(user);
    setActivateOpen(true);
  }, []);

  const handleActiveStatusChange = async (isActive: boolean) => {
    if (!selectedRecord) return;
    try {
      const res = await setUserActiveStatus(Number(selectedRecord.id), isActive);
      if (res.success) {
        toast.success(res.message ?? (isActive ? "User activated" : "User deactivated"));
        tableRef.current?.();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to update status");
    } finally {
      setDeactivateOpen(false);
      setActivateOpen(false);
      setSelectedRecord(null);
    }
  };

  const handleDelete = async () => {
    if (selectedRecord) {
      try {
        const response = await deleteUser(Number(selectedRecord.id));
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
        }
        setDeleteModal(false);
      } catch (err: any) {
        toast.error(err?.response?.data?.message ?? "Delete failed.");
      }
    }
  };

  const columns: ColumnDef<User>[] = useMemo(
    () => [
      IndexColumn<User>(),
      {
        header: "User",
        accessorKey: "firstName",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const user = row.original;
          const fullName = `${user.firstName} ${user.lastName}`.trim();
          return (
            <div className="d-flex align-items-center gap-2">
              <div className="avatar-sm flex-shrink-0">
                <Avatar name={fullName || user.email} image={user.avatar} />
              </div>
              <div className="flex-grow-1 min-w-0">
                <div className="d-flex align-items-center gap-1">
                  {canReadUser ? (
                    <Link
                      to={`/user-management/users/${user.id}`}
                      className="fw-medium text-body text-truncate text-decoration-none"
                    >
                      {fullName || "—"}
                    </Link>
                  ) : (
                    <span className="fw-medium text-body text-truncate">
                      {fullName || "—"}
                    </span>
                  )}
                  {isYou(Number(user.id)) ? (
                    <Badge color="primary" pill className="font-size-10 px-2">
                      You
                    </Badge>
                  ) : null}
                </div>
                <small className="text-muted text-truncate d-block">
                  {user.email}
                </small>
              </div>
            </div>
          );
        },
      },
      {
        header: "Phone",
        accessorKey: "phone",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const phone = row.original.phone;
          return (
            <span className="text-body">
              {phone ? (
                <a href={`tel:${phone}`} className="text-reset">
                  {phone}
                </a>
              ) : (
                <span className="text-muted">—</span>
              )}
            </span>
          );
        },
      },
      {
        header: "Status",
        id: "isActive",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const active = row.original.isActive !== false;
          return active ? (
            <span className="badge bg-success-subtle text-success">Active</span>
          ) : (
            <span className="badge bg-danger-subtle text-danger">Inactive</span>
          );
        },
      },
      {
        header: "Roles",
        accessorKey: "roles",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const roles = row.original.roles;
          if (!roles?.length) {
            return <span className="text-muted">—</span>;
          }
          return (
            <div
              className="d-flex flex-wrap gap-1"
              style={{ maxWidth: "200px" }}
            >
              {roles.map((role) => (
                <Badge
                  key={role.id}
                  className={`${getRoleBadgeClass(role.code)} font-size-11 px-2 py-1`}
                  pill
                >
                  {role.name}
                </Badge>
              ))}
            </div>
          );
        },
      },
      {
        header: "Last Seen",
        accessorKey: "lastSeenAt",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.lastSeenAt ? (
            <DateTime date={row.original.lastSeenAt} />
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        header: "Joined",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) => {
          return <DateTime date={row.original.created_at} />;
        },
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const user = row.original;
          const isActive = user.isActive !== false;
          const isSelf = isYou(Number(user.id));
          const hasAny = canReadUser || canUpdateUser || canDeleteUser;
          if (!hasAny) {
            return <span className="text-muted small">—</span>;
          }
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="User actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canReadUser ? (
                  <DropdownItem
                    tag={Link}
                    to={`/user-management/users/${user.id}`}
                  >
                    <i className="ri-eye-line align-bottom me-2 text-muted" />
                    View
                  </DropdownItem>
                ) : null}
                {canUpdateUser ? (
                  <DropdownItem
                    tag={Link}
                    to={`/user-management/users/edit/${user.id}`}
                  >
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {canUpdateUser && !isSelf ? (
                  isActive ? (
                    <DropdownItem
                      className="text-warning"
                      onClick={() => openDeactivate(user)}
                    >
                      <i className="ri-forbid-line align-bottom me-2" />
                      Deactivate
                    </DropdownItem>
                  ) : (
                    <DropdownItem
                      className="text-success"
                      onClick={() => openActivate(user)}
                    >
                      <i className="ri-checkbox-circle-line align-bottom me-2" />
                      Activate
                    </DropdownItem>
                  )
                ) : null}
                {canDeleteUser ? (
                  <>
                    {canReadUser || canUpdateUser ? (
                      <DropdownItem divider />
                    ) : null}
                    <DropdownItem
                      className="text-danger"
                      onClick={() => onClickDelete(user)}
                    >
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [onClickDelete, openDeactivate, openActivate, canReadUser, canUpdateUser, canDeleteUser, isYou],
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
        description="Deleting this user will also permanently remove their linked employee record, salary structures, bank accounts, and all associated data."
      />
      <ConfirmActionModal
        show={deactivateOpen}
        title="Deactivate user"
        iconClass="ri-user-forbid-line"
        iconColor="text-warning"
        description={`Deactivating ${selectedRecord ? `${selectedRecord.firstName} ${selectedRecord.lastName}`.trim() || selectedRecord.email : "this user"} will immediately block their login access.\n\nThey will also be excluded from payroll runs and attendance tracking until reactivated. This can be reversed at any time.`}
        confirmText="Deactivate"
        confirmColor="warning"
        onConfirm={() => void handleActiveStatusChange(false)}
        onClose={() => { setDeactivateOpen(false); setSelectedRecord(null); }}
      />
      <ConfirmActionModal
        show={activateOpen}
        title="Activate user"
        iconClass="ri-user-follow-line"
        iconColor="text-success"
        description={`${selectedRecord ? `${selectedRecord.firstName} ${selectedRecord.lastName}`.trim() || selectedRecord.email : "This user"} will be able to log in again and will be eligible for payroll runs and attendance tracking.`}
        confirmText="Activate"
        confirmColor="success"
        onConfirm={() => void handleActiveStatusChange(true)}
        onClose={() => { setActivateOpen(false); setSelectedRecord(null); }}
      />

      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Users" pageTitle="User Management" />
          <Card>
            <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
              <div>
                <h5 className="card-title mb-1">Users List</h5>
                <p className="text-muted mb-0 small">
                  Manage team members, roles, and access.
                </p>
              </div>
              {can(Permissions.USER.CREATE) ? (
                <Link
                  to="/user-management/users/create"
                  className="btn btn-primary"
                >
                  <i className="ri-add-line align-bottom me-1"></i> Add User
                </Link>
              ) : null}
            </CardHeader>
            <CardBody>
              <TableContainer
                columns={columns}
                fetchApi={getUsers}
                pageSize={10}
                globalSearch={true}
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-3"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UserList;
